import React, { useState, useEffect } from "react";
import {
  Alert,
  Spinner,
  Modal,
  Button,
} from "react-bootstrap";
import { useHistory } from "react-router-dom";
import ReactSelect from "react-select";
import { Icon } from "../../utils/iconHelper";
import { useEntityLabels } from "../../_context/EntityLabelsContext";
import CourseManagementService, {
  CourseListItem,
  CourseLookups,
} from "../../_services/CourseManagementService";
import CourseFormPanel from "./components/CourseFormPanel";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import "./course-management.css";

const CourseManagementPage: React.FC = () => {
  const { label, labelPlural } = useEntityLabels();
  const courseSectionLabel = label("course", "Course Management");
  const courseNoun = courseSectionLabel.replace(/\s*Management\s*$/i, "") || "Course";
  useDocumentTitle(courseSectionLabel);
  const history = useHistory();

  // Data
  const [courses, setCourses] = useState<CourseListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [courseTypeFilter, setCourseTypeFilter] = useState("");
  const [streamFilter, setStreamFilter] = useState("");
  const [schoolFilter, setSchoolFilter] = useState("");
  const [electiveFilter, setElectiveFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");

  // Lookups
  const [lookups, setLookups] = useState<CourseLookups>({
    isProgramBased: false,
    courseTypes: [],
    streams: [],
    schools: [],
    programs: [],
    terms: [],
  });
  const [programFilter, setProgramFilter] = useState("");
  const [termFilter, setTermFilter] = useState("");

  // Sorting
  const [sortBy, setSortBy] = useState<string>("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  // Panel & modal states
  const [showFormPanel, setShowFormPanel] = useState(false);
  const [editingCourseId, setEditingCourseId] = useState<number | null>(null);
  const [viewOnly, setViewOnly] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingCourse, setDeletingCourse] = useState<CourseListItem | null>(null);

  useEffect(() => {
    loadLookups();
  }, []);

  useEffect(() => {
    fetchCourses();
  }, [currentPage, statusFilter, sortBy, sortDir]);

  const loadLookups = async () => {
    const res = await CourseManagementService.getLookups();
    if (res.success) {
      setLookups(res.data);
    }
  };

  const fetchCourses = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await CourseManagementService.searchCourses({
        page: currentPage,
        limit: pageSize,
        search: searchTerm || undefined,
        courseTypeId: courseTypeFilter ? Number(courseTypeFilter) : undefined,
        streamId: streamFilter ? Number(streamFilter) : undefined,
        schoolId: schoolFilter ? Number(schoolFilter) : undefined,
        programId: programFilter ? Number(programFilter) : undefined,
        termId: termFilter ? Number(termFilter) : undefined,
        isElective: electiveFilter || undefined,
        includeInactive: statusFilter === "all" || statusFilter === "inactive" ? true : undefined,
        sortBy,
        sortDir,
      });
      if (res.success) {
        let filtered = res.data.courses;
        // Client-side filter for "inactive only"
        if (statusFilter === "inactive") {
          filtered = filtered.filter((c) => c.enableStatus !== 1);
        }
        setCourses(filtered);
        setTotalCount(res.data.pagination.total);
      } else {
        setError("Failed to fetch courses");
      }
    } catch (err: any) {
      setError(err.message || "Failed to fetch courses");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchCourses();
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setCourseTypeFilter("");
    setStreamFilter("");
    setSchoolFilter("");
    setProgramFilter("");
    setTermFilter("");
    setElectiveFilter("");
    setStatusFilter("active");
    setCurrentPage(1);
    // Fetch will be triggered by statusFilter change via useEffect
  };

  const handleCreate = () => {
    setEditingCourseId(null);
    setViewOnly(false);
    setShowFormPanel(true);
  };

  const handleEdit = (course: CourseListItem) => {
    setEditingCourseId(course.id);
    setViewOnly(false);
    setShowFormPanel(true);
  };

  const handleView = (course: CourseListItem) => {
    setEditingCourseId(course.id);
    setViewOnly(true);
    setShowFormPanel(true);
  };

  const handleDelete = (course: CourseListItem) => {
    setDeletingCourse(course);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingCourse) return;
    try {
      const res = await CourseManagementService.deleteCourse(deletingCourse.id);
      if (res.success) {
        setSuccess(`Course "${deletingCourse.name}" deleted successfully`);
        setShowDeleteModal(false);
        setDeletingCourse(null);
        fetchCourses();
      } else {
        setError(res.message || "Failed to delete course");
      }
    } catch (err: any) {
      setError(err.message || "Failed to delete course");
    }
  };

  const handleFormSuccess = () => {
    setShowFormPanel(false);
    setSuccess(editingCourseId ? "Course updated successfully" : "Course created successfully");
    setEditingCourseId(null);
    fetchCourses();
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortDir("asc");
    }
    setCurrentPage(1);
  };

  const sortIcon = (column: string) => {
    if (sortBy !== column) return <span className="cm-sort-icon inactive">&#8597;</span>;
    return <span className="cm-sort-icon">{sortDir === "asc" ? "\u25B2" : "\u25BC"}</span>;
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  return (
    <div className="cm-page">
      {/* Header */}
      <div className="cm-header">
        <div className="cm-header-left">
          <span className="cm-header-badge">
            <Icon.Book size={22} />
          </span>
          <div>
            <h1>{courseSectionLabel}</h1>
            <p className="cm-header-sub">
              Manage {courseNoun.toLowerCase()} details, schedules and centre assignments
            </p>
          </div>
        </div>
        <div className="cm-header-actions">
          <button className="cm-btn-create" onClick={handleCreate}>
            <Icon.Plus size={16} />
            Create New {courseNoun}
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="cm-alert cm-alert-error">
          <span>{error}</span>
          <button className="cm-alert-dismiss" onClick={() => setError(null)}>&times;</button>
        </div>
      )}
      {success && (
        <div className="cm-alert cm-alert-success">
          <span>{success}</span>
          <button className="cm-alert-dismiss" onClick={() => setSuccess(null)}>&times;</button>
        </div>
      )}

      {/* Filters */}
      <div className="cm-filters">
        <div className="cm-search-wrap">
          <Icon.Search size={16} className="cm-search-icon" />
          <input
            className="cm-search-input"
            type="text"
            placeholder="Search by name or code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
        </div>
        <select
          className="cm-filter-select"
          value={courseTypeFilter}
          onChange={(e) => setCourseTypeFilter(e.target.value)}
        >
          <option value="">All Types</option>
          {lookups.courseTypes.map((ct) => (
            <option key={ct.id} value={ct.id}>{ct.name}</option>
          ))}
        </select>
        {lookups.isProgramBased && (
          <select
            className="cm-filter-select"
            value={streamFilter}
            onChange={(e) => setStreamFilter(e.target.value)}
          >
            <option value="">All Streams</option>
            {lookups.streams.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        )}
        <select
          className="cm-filter-select"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="all">All</option>
        </select>
        <select
          className="cm-filter-select"
          value={schoolFilter}
          onChange={(e) => setSchoolFilter(e.target.value)}
        >
          <option value="">All {labelPlural("school", "School")}</option>
          {lookups.schools.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
        {lookups.isProgramBased && (
          <>
            <div style={{ minWidth: 260, flex: "0 0 260px" }}>
              <ReactSelect
                isClearable
                placeholder={`All ${labelPlural("program", "Program")}`}
                options={lookups.programs.map((p) => ({
                  value: String(p.id),
                  label: p.code ? `${p.name} (${p.code})` : p.name,
                }))}
                value={
                  programFilter
                    ? (() => {
                        const p = lookups.programs.find((pr) => String(pr.id) === programFilter);
                        return p
                          ? {
                              value: String(p.id),
                              label: p.code ? `${p.name} (${p.code})` : p.name,
                            }
                          : null;
                      })()
                    : null
                }
                onChange={(opt: any) => {
                  setProgramFilter(opt ? opt.value : "");
                  setTermFilter(""); // reset term when program changes
                }}
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: 38,
                    borderRadius: 8,
                    borderColor: "var(--border-strong)",
                  }),
                  menu: (base) => ({ ...base, zIndex: 20 }),
                }}
              />
            </div>
            <div style={{ position: "relative", minWidth: 200, flex: "0 0 200px" }}>
              <ReactSelect
                isClearable
                isDisabled={!programFilter}
                placeholder={`All ${labelPlural("term", "Term")}`}
                options={lookups.terms
                  .filter((t) => programFilter && String(t.programId) === programFilter)
                  .map((t) => ({ value: String(t.id), label: t.name }))}
                value={
                  termFilter
                    ? (() => {
                        const t = lookups.terms.find((tm) => String(tm.id) === termFilter);
                        return t ? { value: String(t.id), label: t.name } : null;
                      })()
                    : null
                }
                onChange={(opt: any) => setTermFilter(opt ? opt.value : "")}
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: 38,
                    borderRadius: 8,
                    borderColor: "var(--border-strong)",
                  }),
                  menu: (base) => ({ ...base, zIndex: 20 }),
                }}
              />
              {!programFilter && (
                <span className="cm-filter-hint">
                  Select a {label("program", "Program").toLowerCase()} first
                </span>
              )}
            </div>
          </>
        )}
        {lookups.isProgramBased && (
          <select
            className="cm-filter-select"
            value={electiveFilter}
            onChange={(e) => setElectiveFilter(e.target.value)}
            style={{ minWidth: 100 }}
          >
            <option value="">Elective?</option>
            <option value="1">Yes</option>
            <option value="0">No</option>
          </select>
        )}
        <div className="cm-filter-actions">
          <button className="cm-btn-apply" onClick={handleSearch}>Apply</button>
          <button className="cm-btn-reset" onClick={handleResetFilters}>Reset</button>
          <span className="cm-count"><strong>{totalCount}</strong> courses</span>
        </div>
      </div>

      {/* Table */}
      <div className="cm-table-container">
        {loading ? (
          <div className="cm-loading">
            <Spinner role="status" animation="border" variant="primary" />
            <p>Loading courses...</p>
          </div>
        ) : courses.length === 0 ? (
          <div className="cm-empty">
            <Icon.Book size={48} style={{ color: "var(--text-faint)" }} />
            <h5>No courses found</h5>
            <p>Try adjusting your filters or create a new course</p>
            <button className="cm-btn-create" onClick={handleCreate}>
              <Icon.Plus size={16} />
              Create Course
            </button>
          </div>
        ) : (
          <>
            <table className="cm-table">
              <thead>
                <tr>
                  <th className={`sortable ${sortBy === "id" ? "active-sort" : ""}`} style={{ width: 60 }} onClick={() => handleSort("id")}>
                    ID{sortIcon("id")}
                  </th>
                  <th className={`sortable ${sortBy === "name" ? "active-sort" : ""}`} onClick={() => handleSort("name")}>
                    Name{sortIcon("name")}
                  </th>
                  <th className={`sortable ${sortBy === "code" ? "active-sort" : ""}`} style={{ width: 130 }} onClick={() => handleSort("code")}>
                    Code{sortIcon("code")}
                  </th>
                  <th className={`sortable ${sortBy === "type" ? "active-sort" : ""}`} style={{ width: 120 }} onClick={() => handleSort("type")}>
                    Type{sortIcon("type")}
                  </th>
                  {lookups.isProgramBased && (
                    <th className={`sortable ${sortBy === "stream" ? "active-sort" : ""}`} style={{ width: 120 }} onClick={() => handleSort("stream")}>
                      Stream{sortIcon("stream")}
                    </th>
                  )}
                  <th style={{ width: 160 }}>{labelPlural("school", "School")}</th>
                  <th className={`sortable ${sortBy === "sessions" ? "active-sort" : ""}`} style={{ width: 90 }} onClick={() => handleSort("sessions")}>
                    Sessions{sortIcon("sessions")}
                  </th>
                  <th className={`sortable ${sortBy === "status" ? "active-sort" : ""}`} style={{ width: 90 }} onClick={() => handleSort("status")}>
                    Status{sortIcon("status")}
                  </th>
                  <th style={{ width: 120 }} className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {courses.map((course) => (
                  <tr key={course.id}>
                    <td className="cm-cell-muted">{course.id}</td>
                    <td><span className="cm-course-name">{course.name}</span></td>
                    <td className="cm-code-cell" title={course.courseShortCode || ""}>{course.courseShortCode || "\u2014"}</td>
                    <td>
                      {course.courseTypeName ? (
                        <span className="cm-type-chip">{course.courseTypeName}</span>
                      ) : (
                        <span className="cm-cell-muted">\u2014</span>
                      )}
                    </td>
                    {lookups.isProgramBased && (
                      <td className="cm-cell-muted">{course.streamName || "\u2014"}</td>
                    )}
                    <td title={course.schools || ""}>
                      {course.schools ? (
                        <div className="cm-centre-chips">
                          {course.schools.split(", ").slice(0, 2).map((c, i) => (
                            <span key={i} className="cm-centre-chip">{c}</span>
                          ))}
                          {course.schools.split(", ").length > 2 && (
                            <span className="cm-centre-more">
                              +{course.schools.split(", ").length - 2}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="cm-cell-muted">\u2014</span>
                      )}
                    </td>
                    <td className="cm-cell-muted">{course.numberOfSessions ?? "\u2014"}</td>
                    <td>
                      {course.enableStatus === 1 ? (
                        <span className="cm-status-active">Active</span>
                      ) : (
                        <span className="cm-status-inactive">Inactive</span>
                      )}
                    </td>
                    <td>
                      <div className="cm-actions">
                        <button
                          className="cm-btn-action cm-btn-docs"
                          onClick={() => history.push(`/course-management/${course.id}/documents`)}
                          title="Course documents"
                        >
                          <Icon.FileText size={14} />
                        </button>
                        <button
                          className="cm-btn-action cm-btn-view"
                          onClick={() => handleView(course)}
                          title="View course"
                        >
                          <Icon.Eye size={14} />
                        </button>
                        <button
                          className="cm-btn-action cm-btn-edit"
                          onClick={() => handleEdit(course)}
                          title="Edit course"
                        >
                          <Icon.Edit2 size={14} />
                        </button>
                        <button
                          className="cm-btn-action cm-btn-delete"
                          onClick={() => handleDelete(course)}
                          title="Delete course"
                        >
                          <Icon.Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="cm-pagination">
                <span className="cm-pagination-info">
                  Showing {startItem}&ndash;{endItem} of {totalCount}
                </span>
                <div className="cm-pagination-controls">
                  <button
                    className="cm-btn-page"
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage((p) => p - 1)}
                  >
                    Previous
                  </button>
                  <span className="cm-page-info">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    className="cm-btn-page"
                    disabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Form Panel */}
      <CourseFormPanel
        show={showFormPanel}
        onHide={() => setShowFormPanel(false)}
        courseId={editingCourseId}
        lookups={lookups}
        onSuccess={handleFormSuccess}
        viewOnly={viewOnly}
      />

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this course?</p>
          {deletingCourse && (
            <Alert variant="warning" className="mb-0">
              <strong>{deletingCourse.name}</strong>
              <br />
              <small className="text-muted">Code: {deletingCourse.courseShortCode}</small>
            </Alert>
          )}
          <p className="mt-3 text-muted small">
            This will mark the course as inactive. It can be re-activated later.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CourseManagementPage;
